/**
 * <cw-aisle>: the aisle page's behaviour (design-handoff/screens/aisle.md, chips.md).
 *
 * - Scrolling the list (v13's onScroll, as is): past 40 px after 40 px of steady downward
 *   scroll, the phone's delivery row tucks away, the shelf title folds and the tabs hide; a
 *   scroll up of more than 3 px brings the tabs back; only the top of the list (<= 4 px)
 *   unfolds everything. Announced as 'cw:fold' {folded, navHidden} for the header, shelf,
 *   rail and tabs.
 * - The chip bar turns white once the title has gone under it (phone, tablet).
 * - Desktop: the top row compacts once the list has scrolled 12 px.
 * - Chips filter the grid and scroll the list to the top. Cards that leave are parked
 *   outside the grid, not hidden in it, so the grid's hairlines (nth-child rules) stay right.
 *   A card carries all its chips (data-chips="|Everyday|Freshly Ground|") and its place in the
 *   full order (data-order). An aisle without an All chip arrives already filtered: the cards
 *   outside its first chip are rendered into the park.
 * - A chip with its own line (data-sub) swaps the line under the title; others restore it.
 * - A chip with its own card order (data-seq) shows its cards in that order (stable sort, so
 *   cards it does not name keep the full order after them).
 * - The next-aisle card names the first aisle with nothing in the basket (not this one).
 * - Back/forward returns to where the list was.
 * - Phones (design-handoff/scroll-and-bars.md, owner 14 Oct): the page scrolls instead of the
 *   list, so the browser can shrink its own bars, and the top moves with the finger (the
 *   delivery row and shelf title are page content; cw-aisle.css). The same rules read the
 *   window there; "folded" is simply the delivery row being out of view (the rail's search
 *   button), and the tabs keep their rule, with the direction taken from the finger.
 */
import { paintNext } from '@cw/aisles';

/** Scroll room (px) a list needs before it may fold: more than the header, shelf and tabs give back. */
const FOLD_ROOM = 240;

/** Phones scroll the page; tablets and desktop keep the frame's own scrollers. */
const PAGE_MQ = window.matchMedia('(max-width: 599.98px)');

class CwAisle extends HTMLElement {
  #last = 0;
  #acc = 0;
  #roomOpen = Infinity;
  #state = { folded: false, navHidden: false };
  #order = [];
  #park = null;
  #fingerY = 0;
  #fingerDown = null;

  connectedCallback() {
    this.list = this.querySelector('[data-cw="scroller"]');
    this.top = this.querySelector('.cw-aisle__top');
    this.chipbar = this.querySelector('[data-cw="chipbar"]');
    this.chiprow = this.querySelector('[data-cw="chiprow"]');
    this.grid = this.querySelector('[data-cw="grid"]');
    this.nextCard = this.querySelector('[data-cw="next"]');
    this.sub = this.querySelector('[data-cw="sub"]');
    this.#park = this.querySelector('[data-cw="park"]');
    if (!this.#park) {
      this.#park = document.createElement('div');
      this.#park.hidden = true;
      this.append(this.#park);
    }
    const cards = [...(this.grid?.children || []), ...this.#park.children];
    this.#order = cards.sort((a, b) => Number(a.dataset.order || 0) - Number(b.dataset.order || 0));
    this.#measure();
    document.fonts?.ready.then(this.#measure);
    // The chip that is on at the start, when it has its own card order: the server drew the
    // full order, so put this chip's order in place now.
    const startChip = this.chiprow?.querySelector('.cw-chip.is-on[data-seq]');
    if (startChip) this.#apply(startChip);

    this.header = document.querySelector('.shopify-section-group-header-group');
    this.list.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    window.addEventListener('touchmove', this.#onTouchMove, { passive: true });
    this.chiprow?.addEventListener('scroll', this.#onChipScroll, { passive: true });
    this.chiprow?.addEventListener('click', this.#onChip);
    document.addEventListener('cw:cart:change', this.#next);
    document.addEventListener('cw:fold:sync', this.#sync);
    window.addEventListener('pagehide', this.#remember);

    this.#restore();
    this.#next();
    this.#announce();
    this.#backLink();
  }

  /**
   * "‹ Basket" above the title after BROWSE on a missing-aisle tile (basket-drawer.md): it
   * reopens the basket. A rail tap or any tab clears it.
   */
  #backLink() {
    const link = this.querySelector('[data-cw="back"]');
    let back = null;
    try {
      back = JSON.parse(sessionStorage.getItem('cw:back') || 'null');
    } catch {
      back = null;
    }
    const clear = () => {
      try {
        sessionStorage.removeItem('cw:back');
      } catch {
        // nothing kept
      }
    };
    if (!link || !back || back.to !== 'basket' || back.aisle !== this.dataset.aisle) return;
    link.querySelector('span').textContent = 'Basket';
    link.hidden = false;
    link.addEventListener('click', (event) => {
      event.preventDefault();
      clear();
      link.hidden = true;
      document.dispatchEvent(new CustomEvent('cw:basket:open', { cancelable: true, detail: { source: link } }));
    });
    document.addEventListener('click', (event) => {
      if (/** @type {HTMLElement} */ (event.target).closest('.cw-rail a, .cw-tabs a, .cw-tabs button')) clear();
    });
  }

  disconnectedCallback() {
    this.list?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('touchstart', this.#onTouchStart);
    window.removeEventListener('touchmove', this.#onTouchMove);
    this.chiprow?.removeEventListener('scroll', this.#onChipScroll);
    this.chiprow?.removeEventListener('click', this.#onChip);
    document.removeEventListener('cw:cart:change', this.#next);
    document.removeEventListener('cw:fold:sync', this.#sync);
    window.removeEventListener('pagehide', this.#remember);
  }

  /** A screen over the page (Order Again) closed with the tabs showing: that is the state now. */
  #sync = () => {
    this.#state = { ...this.#state, navHidden: false };
  };

  #announce() {
    document.dispatchEvent(new CustomEvent('cw:fold', { detail: { ...this.#state } }));
    for (const shelf of document.querySelectorAll('.cw-aisle__band cw-shelf')) {
      shelf.toggleAttribute('data-folded', this.#state.folded);
    }
  }

  #set(patch) {
    const next = { ...this.#state, ...patch };
    // Phones: the delivery row scrolls away with the page, so "folded" is simply "the row is
    // out of view" (it shows the rail's search button); the tabs keep their own rule.
    if (this.#page) next.folded = !this.header || this.header.getBoundingClientRect().bottom <= 0;
    if (next.folded === this.#state.folded && next.navHidden === this.#state.navHidden) return;
    this.#state = next;
    this.#announce();
  }


  /** Phones: the document scrolls, else the list. */
  get #page() {
    return PAGE_MQ.matches;
  }

  get #y() {
    return this.#page ? window.scrollY : this.list.scrollTop;
  }

  #scrollTo(y) {
    if (this.#page) window.scrollTo({ top: y });
    else this.list.scrollTo({ top: y });
  }


  /**
   * Phones: Chrome on Android moves the page back a few px while its address bar
   * slides away, before it reports any resize (owner's readout, 14 Oct: 158 -> 144 while
   * scrolling down). So the direction comes from the finger: while the last touch gesture
   * scrolls the page down (finger moving up, and the fling after it, until the next touch),
   * a step back is the browser's, and brings nothing back. Mouse and keyboard are unaffected.
   */
  #onTouchStart = (event) => {
    this.#fingerY = event.touches[0]?.clientY ?? 0;
    this.#fingerDown = null;
  };

  #onTouchMove = (event) => {
    const fy = event.touches[0]?.clientY ?? this.#fingerY;
    if (Math.abs(fy - this.#fingerY) < 2) return;
    this.#fingerDown = fy < this.#fingerY;
    this.#fingerY = fy;
  };

  #onScroll = () => {
    const y = this.#y;
    const last = this.#last;
    // A repeated position (some browsers send two events for one frame) is not a reversal.
    if (y === last) return;
    this.#last = y;
    if (this.#page && y < last && this.#fingerDown) return;
    // A short list (a chip with a few cards) never folds: hiding the bars makes it fit, the
    // browser pulls the scroll back, that reads as a scroll up, the bars return, and the page
    // bounces for ever. Folding needs room to scroll past what the bars give back.
    // Measured with the bars showing (folding makes the list taller and the room smaller).
    const room = this.#page
      ? document.documentElement.scrollHeight - window.innerHeight
      : this.list.scrollHeight - this.list.clientHeight;
    if (!this.#state.folded) this.#roomOpen = room;
    if (this.#roomOpen < FOLD_ROOM) {
      this.#acc = 0;
      this.#set({ folded: false, navHidden: false });
    } else if (y < last && y >= room - 2) {
      // The browser clamping the scroll after the bars hid (at the end of the list): not the
      // shopper scrolling up, so nothing changes.
    } else if (y <= 4) {
      this.#acc = 0;
      this.#set({ folded: false, navHidden: false });
    } else {
      this.#acc = y > last ? this.#acc + (y - last) : 0;
      if (y > 40 && this.#acc >= 40) this.#set({ folded: true, navHidden: true });
      else if (y < last - 3) this.#set({ navHidden: false });
    }
    if (this.#page) this.#set({});
    // Phone/tablet: the chip bar is "stuck" once the title is gone under it.
    if (this.chipbar) {
      const edge = this.#page
        ? this.querySelector('.cw-aisle__band')?.getBoundingClientRect().bottom || 0
        : this.list.getBoundingClientRect().top;
      const stuck = this.chipbar.getBoundingClientRect().top <= edge + 0.5 && y > 0;
      this.chipbar.classList.toggle('is-stuck', stuck);
    }
    // Desktop: the top row compacts after 12 px.
    this.top?.classList.toggle('is-compact', y > 12);
  };

  /** Desktop: the title folds by its real height (cw-aisle.css --cw-aisle-head-h). */
  #measure = () => {
    const heading = this.querySelector('.cw-aisle__heading');
    if (heading?.scrollHeight) this.top?.style.setProperty('--cw-aisle-head-h', `${heading.scrollHeight}px`);
  };

  #onChipScroll = () => {
    this.chiprow.classList.toggle('is-scrolled', this.chiprow.scrollLeft > 4);
  };

  #onChip = (event) => {
    const chip = /** @type {HTMLElement} */ (event.target).closest('.cw-chip');
    if (!chip) return;
    this.#apply(chip);
    this.#scrollTo(0);
  };

  /** Show a chip's cards (in its own order, when it has one) and its line under the title. */
  #apply(chip) {
    for (const c of this.chiprow.querySelectorAll('.cw-chip')) {
      const on = c === chip;
      c.classList.toggle('is-on', on);
      c.setAttribute('aria-pressed', String(on));
    }
    const needle = `|${chip.dataset.chip}|`;
    const all = chip.hasAttribute('data-all');
    // A chip with its own order (data-seq, product handles) puts those first, in that order.
    const seq = (chip.dataset.seq || '').split(',').filter(Boolean);
    const rank = (card) => {
      const i = seq.indexOf(card.dataset.handle);
      return i < 0 ? seq.length : i;
    };
    const order = seq.length ? [...this.#order].sort((a, b) => rank(a) - rank(b)) : this.#order;
    for (const card of order) {
      const keep = all || (card.dataset.chips || '').includes(needle);
      if (keep) this.grid.append(card);
      else this.#park.append(card);
    }
    if (this.sub) {
      this.sub.textContent = chip.dataset.sub || this.sub.dataset.default || '';
      this.#measure();
    }
  }

  /** "You may still need" / "Still missing" / "You may also want" + the first empty aisle. */
  #next = () => paintNext(this.nextCard, this.dataset.aisle);

  #key() {
    return `cw:scroll:${location.pathname}${location.search}`;
  }

  #remember = () => {
    try {
      sessionStorage.setItem(this.#key(), String(Math.round(this.#y)));
    } catch {
      // nothing to restore next time
    }
  };

  #restore() {
    const nav = performance.getEntriesByType?.('navigation')?.[0];
    if (!nav || nav.type !== 'back_forward') return;
    let y = 0;
    try {
      y = Number(sessionStorage.getItem(this.#key()) || 0);
    } catch {
      y = 0;
    }
    if (y > 0) {
      this.#scrollTo(y);
      this.#last = this.#y;
    }
  }
}

if (!customElements.get('cw-aisle')) customElements.define('cw-aisle', CwAisle);
